import { useState, useMemo } from "react";
import {
  Search, X, Code, Image as ImageIcon,
  // ---- curated CMS icon set (EXPLICIT imports so lucide-react tree-shakes) ----
  // People & spirit
  Sparkles, Heart, HeartHandshake, Brain, Users, User, UserCircle, UserPlus, UserCheck,
  // Wellness / nature
  Sun, Moon, Star, Flower, Flower2, Leaf, Trees, Mountain, Wind, Waves, Cloud,
  // Process / steps
  Compass, Map as MapIcon, MapPin, Target, Crosshair, Route, ArrowRight, ArrowUpRight,
  // Energy / change
  Zap, Flame, Bolt, Atom, Infinity as InfinityIcon, RefreshCcw, Repeat,
  // Sessions / media
  Headphones, Mic, Radio, Video, Play, BookOpen, Book, GraduationCap, FileText, Library, ClipboardList,
  // Time / calendar
  Calendar, CalendarHeart, Clock, Hourglass,
  // Quality / trust
  Award, Trophy, Crown, Medal, ShieldCheck, Shield, Lock, KeyRound, CheckCircle2, BadgeCheck,
  // Communication
  MessageCircle, MessageSquare, MessagesSquare, Mail, Send, Phone,
  // Hands / touch
  Hand, Handshake, ThumbsUp, HelpingHand,
  // Misc tools
  Lightbulb, Eye, Headset, Hash,
  // Extra icons stored by the CMS / gamification (rewards, quests, achievements, problems)
  Activity, Apple, Battery, CalendarCheck, CloudRain, Droplet, EyeOff, Gem, Sprout, TreePine, Gift, Tag, Music,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";

/**
 * Curated map of name -> Lucide component for the therapy/wellness CMS.
 *
 * IMPORTANT: this is an EXPLICIT allow-list. It replaces the old
 * `import * as Icons from "lucide-react"` which forced the ENTIRE ~2.3MB icon
 * library into the main bundle. Only the icons listed here ship.
 *
 * It must cover every icon that can be stored in the DB (section/benefit/
 * technique/problem/poiana `icon` fields, reward/quest/achievement icons).
 * Unknown names fall back to DEFAULT_ICON (Sparkles).
 *
 * This map is shared: the public pages (Forest, Techniques, ProblemDetail) and
 * the panels (AdminRewards, GamificationPanel) import it so any admin-picked
 * icon resolves identically everywhere.
 */
export const ICONS = {
  Sparkles, Heart, HeartHandshake, Brain, Users, User, UserCircle, UserPlus, UserCheck,
  Sun, Moon, Star, Flower, Flower2, Leaf, Trees, Mountain, Wind, Waves, Cloud,
  Compass, Map: MapIcon, MapPin, Target, Crosshair, Route, ArrowRight, ArrowUpRight,
  Zap, Flame, Bolt, Atom, Infinity: InfinityIcon, RefreshCcw, Repeat,
  Headphones, Mic, Radio, Video, Play, BookOpen, Book, GraduationCap, FileText, Library, ClipboardList,
  Calendar, CalendarHeart, Clock, Hourglass,
  Award, Trophy, Crown, Medal, ShieldCheck, Shield, Lock, KeyRound, CheckCircle2, BadgeCheck,
  MessageCircle, MessageSquare, MessagesSquare, Mail, Send, Phone,
  Hand, Handshake, ThumbsUp, HelpingHand,
  Lightbulb, Eye, Headset, Hash,
  Activity, Apple, Battery, CalendarCheck, CloudRain, Droplet, EyeOff, Gem, Sprout, TreePine, Gift, Tag, Music,
};

/** Safe fallback for unknown/legacy icon names. */
export const DEFAULT_ICON = Sparkles;

/**
 * Sanitizează un fragment SVG înainte de stocare/afișare.
 *
 * SVG-ul custom e randat cu dangerouslySetInnerHTML pe pagini PUBLICE, deci un
 * SVG malițios (<script>, <foreignObject><img onerror=…>, handler-e on*, href
 * „javascript:") ar putea executa JS în browserul oricărui vizitator. Parsăm
 * inert (DOMParser fără browsing context => fără load de resurse / fără execuție)
 * și eliminăm elementele periculoase, atributele on* și href-urile active.
 * Întoarce "" dacă nu e un <svg> valid.
 */
const SVG_DISALLOWED_TAGS = new Set([
  "script", "foreignobject", "iframe", "image", "use", "a",
  "animate", "animatetransform", "animatemotion", "set", "handler", "style",
]);

export function sanitizeSvg(raw) {
  if (typeof raw !== "string") return "";
  const trimmed = raw.trim();
  if (!trimmed.startsWith("<svg")) return "";
  if (typeof DOMParser === "undefined") {
    // Fallback pentru medii fără DOM: strip minimal pe elemente/atribute active.
    return trimmed
      .replace(/<\/?(script|foreignobject|iframe|use|image|a|animate\w*|set|handler|style)\b[^>]*>/gi, "")
      .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
      .replace(/\s(?:xlink:)?href\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, (m, v) =>
        /javascript:|data:/i.test(v) ? "" : m);
  }
  let svg;
  try {
    const doc = new DOMParser().parseFromString(trimmed, "text/html");
    svg = doc.body.querySelector("svg");
  } catch {
    return "";
  }
  if (!svg) return "";
  const scrub = (node) => {
    Array.from(node.children).forEach((child) => {
      if (SVG_DISALLOWED_TAGS.has(child.tagName.toLowerCase())) {
        child.remove();
        return;
      }
      Array.from(child.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (name.startsWith("on")) child.removeAttribute(attr.name);
        else if ((name === "href" || name === "xlink:href") && /^\s*(javascript|data):/i.test(attr.value || "")) child.removeAttribute(attr.name);
      });
      scrub(child);
    });
  };
  Array.from(svg.attributes).forEach((attr) => {
    if (attr.name.toLowerCase().startsWith("on")) svg.removeAttribute(attr.name);
  });
  scrub(svg);
  return svg.outerHTML;
}

/** Resolve a stored icon name to a component, falling back to Sparkles. */
export function resolveIcon(name) {
  return (name && ICONS[name]) || DEFAULT_ICON;
}

const CURATED = Object.keys(ICONS);

function LucideTab({ value, onChange, onClose }) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return CURATED;
    return CURATED.filter((n) => n.toLowerCase().includes(q));
  }, [search]);

  return (
    <div>
      <div className="relative mb-4">
        <Search size={14} className="absolute top-1/2 -translate-y-1/2 left-3" style={{ color: "var(--ct-text-dim)" }} />
        <input
          autoFocus
          className="premium-input pl-9"
          placeholder="Caută iconuri (ex: heart, star, leaf)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="icon-picker-search"
        />
      </div>
      <div className="max-h-[420px] overflow-y-auto pr-1">
        <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
          {filtered.map((name) => {
            const Ic = ICONS[name] || DEFAULT_ICON;
            const active = value === name;
            return (
              <button
                key={name}
                type="button"
                onClick={() => { onChange(name); onClose?.(); }}
                title={name}
                data-testid={`icon-option-${name}`}
                className={`aspect-square rounded-lg border flex flex-col items-center justify-center gap-1 transition-all hover:scale-105 ${active ? "border-[var(--ct-gold)] shadow-md" : ""}`}
                style={{ borderColor: active ? "var(--ct-gold)" : "var(--ct-border)", background: active ? "rgba(212,175,55,0.1)" : "var(--ct-surface)" }}
              >
                <Ic size={18} style={{ color: active ? "var(--ct-emerald)" : "var(--ct-text-muted)" }} />
                <span className="text-[8px] leading-none truncate w-full text-center px-1" style={{ color: "var(--ct-text-dim)" }}>{name}</span>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <p className="col-span-full text-sm py-6 text-center" style={{ color: "var(--ct-text-muted)" }}>Niciun icon găsit pentru &bdquo;{search}&rdquo;.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function SvgTab({ value, onChange }) {
  const [svg, setSvg] = useState(value && value.startsWith("<svg") ? value : "");
  const clean = sanitizeSvg(svg);
  return (
    <div>
      <p className="text-sm mb-3" style={{ color: "var(--ct-text-muted)" }}>
        Lipește cod SVG complet (înclusiv tag-ul <code className="text-xs px-1 rounded" style={{ background: "var(--ct-bg-soft)" }}>&lt;svg&gt;</code>). Va fi afișat acolo unde apare iconul.
      </p>
      <textarea
        rows={8}
        className="premium-input font-mono text-xs"
        placeholder='<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/></svg>'
        value={svg}
        onChange={(e) => setSvg(e.target.value)}
        data-testid="icon-picker-svg"
      />
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--ct-text-muted)" }}>Previzualizare:</span>
          <div className="w-10 h-10 rounded-lg border flex items-center justify-center" style={{ borderColor: "var(--ct-border)", background: "var(--ct-surface)", color: "var(--ct-link)" }} dangerouslySetInnerHTML={{ __html: clean || '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>' }} />
        </div>
        <button type="button" className="btn-gold" onClick={() => onChange(clean)} data-testid="icon-picker-save-svg" disabled={!clean}>
          Salvează SVG
        </button>
      </div>
    </div>
  );
}

/**
 * Renders the icon based on stored value: either a Lucide name or raw SVG markup.
 */
export function RenderIcon({ value, size = 18, color, className }) {
  if (!value) return <DEFAULT_ICON size={size} className={className} style={{ color }} />;
  if (typeof value === "string" && value.startsWith("<svg")) {
    // Sanitizare defensivă și la RANDARE (nu doar la salvare): apără și
    // vizitatorii de valori SVG malițioase salvate pe altă cale în DB.
    const clean = sanitizeSvg(value);
    if (!clean) return <DEFAULT_ICON size={size} className={className} style={{ color }} />;
    return <span className={className} style={{ display: "inline-flex", width: size, height: size, color }} dangerouslySetInnerHTML={{ __html: clean }} />;
  }
  const Ic = ICONS[value] || DEFAULT_ICON;
  return <Ic size={size} className={className} style={{ color }} />;
}

export default function IconPicker({ value, onChange, label = "Icon" }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("lucide");
  const isSvg = typeof value === "string" && value.startsWith("<svg");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" data-testid="icon-picker-open" className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg border w-full text-left transition-colors hover:border-[var(--ct-gold)]" style={{ background: "var(--ct-surface)", borderColor: "var(--ct-border)" }}>
          <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "var(--ct-bg-soft)", color: "var(--ct-link)" }}>
            <RenderIcon value={value} size={18} />
          </span>
          <span className="flex flex-col">
            <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--ct-text-dim)" }}>{label}</span>
            <span className="text-sm font-medium" style={{ color: "var(--ct-text)" }}>
              {!value ? "Alege un icon" : isSvg ? "SVG custom" : value}
            </span>
          </span>
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Alege un icon</DialogTitle>
          <DialogDescription>Selectează din lista Lucide sau lipește propriul SVG.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 mb-4 border-b" style={{ borderColor: "var(--ct-border)" }}>
          <button type="button" onClick={() => setTab("lucide")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "lucide" ? "" : "border-transparent"}`} style={{ borderColor: tab === "lucide" ? "var(--ct-gold)" : "transparent", color: tab === "lucide" ? "var(--ct-emerald)" : "var(--ct-text-muted)" }} data-testid="icon-picker-tab-lucide">
            <ImageIcon size={14} className="inline mr-1.5" /> Listă iconuri
          </button>
          <button type="button" onClick={() => setTab("svg")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "svg" ? "" : "border-transparent"}`} style={{ borderColor: tab === "svg" ? "var(--ct-gold)" : "transparent", color: tab === "svg" ? "var(--ct-emerald)" : "var(--ct-text-muted)" }} data-testid="icon-picker-tab-svg">
            <Code size={14} className="inline mr-1.5" /> SVG custom
          </button>
        </div>
        {tab === "lucide" && <LucideTab value={value} onChange={onChange} onClose={() => setOpen(false)} />}
        {tab === "svg" && <SvgTab value={value} onChange={(v) => { onChange(v); setOpen(false); }} />}
        {value && (
          <button type="button" onClick={() => { onChange(""); setOpen(false); }} className="mt-4 text-xs inline-flex items-center gap-1.5" style={{ color: "var(--ct-text-muted)" }} data-testid="icon-picker-clear">
            <X size={12} /> Șterge iconul
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
